import { motion } from 'framer-motion';
import {
  INTERNATIONAL_EXPANSION_GROUP,
  INTERNATIONAL_TIER,
  internationalTotal,
} from '@/lib/roi/market-data';
import { CountUp } from './CountUp';

const formatCountedARR = (n: number) => `£${Math.round(n)}m`;

const formatHeadlineARR = (n: number) => `£${Math.round(n)}m ARR`;

/**
 * International expansion TAM — priority markets beyond the UK.
 * Sibling tab to Market size (UK); each market segment displayed as an
 * equal-weight market card, with a wider total card completing the final
 * row on wide screens.
 */
export function International({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const segments = [...INTERNATIONAL_EXPANSION_GROUP.segments].sort(
    (a, b) => b.arr - a.arr,
  );
  const total = internationalTotal();

  return (
    <section className={embedded ? undefined : 'mx-auto w-full max-w-6xl px-8'}>
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
          <span className="inline-flex items-center rounded-md border border-accent-blue-border bg-accent-blue-bg px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-brand-bright">
            {INTERNATIONAL_TIER.acronym}
          </span>
          <h2 className="text-2xl font-semibold leading-tight text-ink-strong sm:text-3xl">
            {INTERNATIONAL_TIER.title}
          </h2>
          <span className="font-semibold tabular-nums text-brand-500 [font-size:28px] [line-height:1] sm:[font-size:32px]">
            {INTERNATIONAL_TIER.headlineValue !== undefined ? (
              <CountUp
                target={INTERNATIONAL_TIER.headlineValue}
                duration={1.6}
                delay={0.3}
                formatter={formatHeadlineARR}
              />
            ) : (
              INTERNATIONAL_TIER.headline
            )}
          </span>
        </div>
        <p className="max-w-2xl text-sm leading-relaxed text-ink-muted">
          {INTERNATIONAL_TIER.scope}
        </p>
      </motion.header>

      {/* 10 markets plus a double-width total complete a 4×3 desktop grid. */}
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {segments.map((segment, index) => (
          <CountryCard
            key={segment.label}
            name={segment.label}
            arr={segment.arr}
            detail={segment.detail}
            index={index}
          />
        ))}
        <TotalCard total={total} index={segments.length} />
      </div>
    </section>
  );
}

function CountryCard({
  name,
  arr,
  detail,
  index,
}: {
  name: string;
  arr: number;
  detail?: string;
  index: number;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -3 }}
      transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.04,
      }}
      className="pitch-card pitch-card-interactive flex flex-col gap-3 rounded-xl p-5 sm:p-6"
    >
      <span className="font-semibold tabular-nums text-brand-500 [font-size:32px] [line-height:1] sm:[font-size:36px]">
        <CountUp
          target={arr}
          duration={1.4}
          delay={0.3 + index * 0.05}
          formatter={formatCountedARR}
        />
      </span>
      <span className="text-xs font-medium uppercase tracking-[0.12em] text-ink-strong">
        {name}
      </span>
      {detail ? (
        <span className="text-xs leading-snug text-ink-muted">{detail}</span>
      ) : null}
    </motion.article>
  );
}

function TotalCard({ total, index }: { total: number; index: number }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -3 }}
      transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.04,
      }}
      className="flex flex-col gap-3 rounded-xl border border-accent-blue-border bg-accent-blue-bg p-5 sm:col-span-2 sm:p-6"
    >
      <span className="font-semibold tabular-nums text-brand-bright [font-size:32px] [line-height:1] sm:[font-size:36px]">
        <CountUp
          target={total}
          duration={1.8}
          delay={0.3 + index * 0.05}
          formatter={formatCountedARR}
        />
      </span>
      <span className="text-xs font-medium uppercase tracking-[0.12em] text-brand-bright">
        Total international TAM
      </span>
    </motion.article>
  );
}
